// src/axios.js
import axios from 'axios';
import { useConfig } from './composables/useConfig.js';

// Obtener configuración
const { config, loadConfig } = useConfig();

// Crear una instancia de axios con configuración inicial
const axiosInstance = axios.create({
    baseURL: config.api.baseURL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    timeout: config.api.timeout,
});

// Función para actualizar la configuración de axios
const updateAxiosConfig = () => {
    axiosInstance.defaults.baseURL = config.api.baseURL;
    axiosInstance.defaults.timeout = config.api.timeout;
};

// Cargar configuración y actualizar axios
loadConfig().then(() => {
    updateAxiosConfig();
    console.log('Configuración de Axios actualizada:', {
        baseURL: axiosInstance.defaults.baseURL,
        timeout: axiosInstance.defaults.timeout
    });
});

// Variable para evitar múltiples renovaciones simultáneas
let isRefreshing = false;
let failedQueue = [];

// Función para procesar la cola de peticiones fallidas
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Interceptor para agregar el token a las solicitudes
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para manejar respuestas y renovación automática de tokens
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Si es un error 401 y no es una petición de renovación o check-token
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/api/check-token') && !originalRequest.url?.includes('/api/login')) {
            if (isRefreshing) {
                // Si ya se está renovando, agregar a la cola
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Intentar renovar el token
                const refreshResponse = await axiosInstance.post('/api/refresh-token');
                const { token, expires_at, user } = refreshResponse.data;

                // Actualizar el token en localStorage
                localStorage.setItem('auth_token', token);
                localStorage.setItem('token_expires_at', expires_at);
                localStorage.setItem('user_data', JSON.stringify(user));

                // Procesar la cola de peticiones fallidas
                processQueue(null, token);

                // Reintentar la petición original
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(originalRequest);

            } catch (refreshError) {
                // Si la renovación falla, limpiar datos y redirigir
                processQueue(refreshError, null);
                
                // Limpiar datos de autenticación
                localStorage.removeItem('auth_token');
                localStorage.removeItem('token_expires_at');
                localStorage.removeItem('user_data');

                // Redirigir al login si no estamos ya ahí
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Para otros errores, simplemente rechazar
        return Promise.reject(error);
    }
);

export default axiosInstance;
export { updateAxiosConfig };
